import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { TabComponent } from '../../../../shared/components/tabs/tab/tab.component';
import { TabsComponent } from '../../../../shared/components/tabs/tabs.component';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { changeTime } from '../../../../shared/utilities/utility-functions';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TextareaModule } from 'primeng/textarea';
import { BasicTableComponent } from '../basic-table/basic-table.component';
import { BasicTablePipe } from '../basic-table/basic-table.pipe';
import { InfoCardComponent } from '../info-card/info-card.component';
import { InfoCardPipe } from '../info-card/info-card.pipe';
import { DataNotFoundComponent } from '../../../../shared/components/data-not-found/data-not-found.component';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { UserRole } from '../../../../core/auth/enums/userRoles';
import { TagComponent } from '../../../../shared/components/tag/tag.component';

interface Notification {
  Id?: number;
  Uuid?: string;
  AccountId?: number;
  JobId?: number;
  RideId?: number;
  Status?: string;
  Title?: string;
  Body?: string;
  PushNotificationBody?: any;
  CreatedByAccountId?: number;
  CreatedAtUtc?: Date;
  ReadAtUtc?: Date;
  CreatedAtUtcTimeStamp?: number;
  CreatedByAccountFirstName?: string;
  CreatedByAccountLastName?: string;
  StudentId?: number;
  NotificationType?: string;
  PortalType?: string;
  ParentId?: number;
  ParentStatus?: any;
  BlueprintId?: number;
}

@Component({
  selector: 'app-contractor-dialog',
  standalone: true,
  imports: [CommonModule, BasicTableComponent, InfoCardComponent, ButtonComponent, TabsComponent, TabComponent, SelectModule, TextareaModule, BasicTablePipe, InfoCardPipe, FormsModule, DataNotFoundComponent, TagComponent],
  templateUrl: './contractor-dialog.component.html',
  styleUrl: './contractor-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly authService = inject(AuthService);

  dropdownOptions = [{ name: 'Health and safety head-ups' }, { name: 'Start Ride' }, { name: 'Schedule Update' }, { name: 'Reminder' }, { name: 'Friendly Reminder' }];

  notifications = signal<any>({});
  notificationTitle = '';
  notificationBody = '';
  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  userRoles = UserRole;

  appSettings = this.authService.appSetting();

  objectKeys = Object.keys;

  ngOnInit() {
    this.getDriverNotifications();
  }

  getDriverNotifications() {
    this.requestBuilder.get(`notifications/notifications-partner/${this.config.data.driverId}`).subscribe(res => {
      this.notifications.set(
        this.categorizeNotificationsByDate(
          res.data.Notifications.map((n: Notification) => {
            return { ...n, CreatedAtUtc: changeTime(n.CreatedAtUtc + 'z', 'MM/DD/YYYY hh:mm A', this.config.data.timezoneOffsetInSeconds) };
          }),
        ),
      );
    });
  }

  sendNotification(form: NgForm) {
    const body = {
      Title: this.notificationTitle,
      Body: this.notificationBody,
    };
    this.requestBuilder.post(`notifications/notifications-partner/${this.config.data.driverId}`, body).subscribe(res => {
      form.resetForm();
      const newNotification: Notification = {
        Title: body.Title,
        Body: body.Body,
        CreatedByAccountFirstName: JSON.parse(localStorage.getItem('apiKey') ?? '{first_name":"Adroit"}')['first_name'],
        CreatedByAccountLastName: JSON.parse(localStorage.getItem('apiKey') ?? '{last_name":"Bot"}')['last_name'],
        CreatedAtUtc: new Date(changeTime(new Date().toISOString(), 'MM/DD/YYYY hh:mm A', this.config.data.timezoneOffsetInSeconds)),
      };
      const flatList = Object.values(this.notifications()).flat();
      const updated = [...flatList, newNotification];
      this.notifications.set(this.categorizeNotificationsByDate(updated as any));
      this.notificationTitle = '';
      this.notificationBody = '';
    });
  }

  categorizeNotificationsByDate(notifications: Notification[]): { [key: string]: Notification[] } {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    return notifications.reduce(
      (acc, item) => {
        const itemDate = new Date(item.CreatedAtUtc || '');
        const itemDay = itemDate.toDateString();

        let label: string;
        if (itemDay === today.toDateString()) {
          label = 'Today';
        } else if (itemDay === yesterday.toDateString()) {
          label = 'Yesterday';
        } else {
          label = formatDate(itemDate, 'MM/dd/yyyy', 'en-US');
        }

        if (!acc[label]) {
          acc[label] = [];
        }
        acc[label].push(item);

        return acc;
      },
      {} as { [key: string]: Notification[] },
    );
  }
}
